/**
 * ShareButton — captures the complete view state and creates a share link.
 *
 * Collects:
 * 1. URL search params (branch, nodes, panel, tab, filters)
 * 2. Full component state via ViewStateContext (layout, DAG viewport, AG Grid columns, etc.)
 *
 * The combined payload is POSTed to the share API and the resulting link
 * is copied to clipboard.
 */

import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Tooltip } from "@radix-ui/themes";

import { useProvenanceContext } from "../context/ProvenanceContext";
import { useViewState } from "../context/ViewStateContext";
import { createShareLink } from "../api/share";

export function ShareButton() {
  const { projectId } = useProvenanceContext();
  const { getFullViewState } = useViewState();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<"idle" | "loading" | "copied" | "error">("idle");

  const handleShare = useCallback(() => {
    setStatus("loading");

    const config: Record<string, unknown> = { project_id: projectId };
    searchParams.forEach((value, key) => { config[key] = value; });
    config.viewState = getFullViewState();

    const urlPromise = createShareLink(config).then(({ share_id }) => {
      const base = import.meta.env.BASE_URL.replace(/\/$/, "");
      return `${window.location.origin}${base}/share/${share_id}`;
    });

    // ClipboardItem with a lazy Blob promise — registers the write
    // synchronously within the user gesture, resolves content later.
    if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
      const item = new ClipboardItem({
        "text/plain": urlPromise.then(url => new Blob([url], { type: "text/plain" })),
      });
      navigator.clipboard.write([item])
        .then(() => { setStatus("copied"); setTimeout(() => setStatus("idle"), 2500); })
        .catch((err) => { console.error("Share failed:", err); setStatus("error"); setTimeout(() => setStatus("idle"), 3000); });
      return;
    }

    // Fallback for browsers without ClipboardItem
    urlPromise
      .then((url) => {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.cssText = "position:fixed;left:-9999px;top:-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setStatus("copied");
        setTimeout(() => setStatus("idle"), 2500);
      })
      .catch((err) => { console.error("Share failed:", err); setStatus("error"); setTimeout(() => setStatus("idle"), 3000); });
  }, [projectId, searchParams, getFullViewState]);

  const label = status === "copied" ? "Copied!" : status === "error" ? "Failed" : "Share";

  return (
    <Tooltip content="Copy shareable link to clipboard">
      <Button
        variant="ghost"
        size="1"
        color={status === "copied" ? "green" : status === "error" ? "red" : "gray"}
        onClick={handleShare}
        disabled={status === "loading"}
      >
        <svg width="14" height="14" viewBox="0 0 15 15" fill="none">
          <path
            d="M5 7.5a2.5 2.5 0 1 1 5 0 2.5 2.5 0 0 1-5 0Zm6.354-3.854a.5.5 0 0 0-.708 0l-1.5 1.5a.5.5 0 0 0 .708.708L11 4.707V7.5a3.5 3.5 0 0 1-7 0V4.707L2.854 5.854a.5.5 0 1 0-.708-.708l1.5-1.5a.5.5 0 0 0 0-.707"
            fill="currentColor"
          />
        </svg>
        {label}
      </Button>
    </Tooltip>
  );
}
